/**
 * Client-side DOCX/PPTX rezip utilities.
 *
 * After server returns modified text XMLs, replace them into the original zip
 * and generate the output file. Preserves all non-text files (media, theme, etc.).
 */

import JSZip from 'jszip';

export const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const PPTX_MIME = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

/**
 * Replace given XMLs into original zip, regenerate ArrayBuffer.
 * Paths not present in original are SKIPPED with warning (no silent add).
 */
export async function replaceXmlsAndRezip(
    originalArrayBuffer: ArrayBuffer,
    modifiedXmls: Record<string, string>,
    mimeType: string,
): Promise<ArrayBuffer> {
    const zip = await JSZip.loadAsync(originalArrayBuffer);

    for (const [path, content] of Object.entries(modifiedXmls)) {
        if (!zip.file(path)) {
            console.warn('[rezip] Path not found in original zip, skipping:', path);
            continue;
        }
        zip.file(path, content);
    }

    return await zip.generateAsync({
        type: 'arraybuffer',
        mimeType,
        compression: 'DEFLATE',
        compressionOptions: { level: 9 },
    });
}

/**
 * Download buffer as file.
 */
export function downloadBuffer(buffer: ArrayBuffer, fileName: string, mimeType: string): void {
    const blob = new Blob([buffer], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Convenience: rezip + trigger download in one call.
 */
export async function rezipAndDownload(
    originalArrayBuffer: ArrayBuffer,
    modifiedXmls: Record<string, string>,
    outputFileName: string,
    format: 'docx' | 'pptx',
): Promise<void> {
    const mimeType = format === 'docx' ? DOCX_MIME : PPTX_MIME;
    const buffer = await replaceXmlsAndRezip(originalArrayBuffer, modifiedXmls, mimeType);
    downloadBuffer(buffer, outputFileName, mimeType);
}
